import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabaseClient';
import { logActivity } from '../../../utils/activityLogger';
import { formatCurrency } from '../../../utils/formatters';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { useSelector } from 'react-redux';
import AIAttachmentUploader from '../../../components/AIAttachmentUploader';

/**
 * Modal component for logging new maintenance records or editing existing ones.
 */
const AddMaintenanceModal = ({ isOpen, onClose, assetTag, onSuccess, recordToEdit = null }) => {
    const { t } = useTranslation();
    const [maintenanceDate, setMaintenanceDate] = useState(new Date().toISOString().split('T')[0]);
    const [type, setType] = useState('');
    const [vendor, setVendor] = useState('');
    const [cost, setCost] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [autofillNotice, setAutofillNotice] = useState(null);
    
    // State for managing document attachments unified with the maintenance log
    const [attachedFile, setAttachedFile] = useState(null);
    const [attachmentMeta, setAttachmentMeta] = useState(null);

    useEffect(() => {
        if (recordToEdit) {
            setMaintenanceDate(recordToEdit.maintenance_date);
            setType(recordToEdit.type || '');
            setVendor(recordToEdit.vendor || recordToEdit.performed_by || '');
            setCost(recordToEdit.cost ? String(recordToEdit.cost) : '');
            setDescription(recordToEdit.description || '');
        } else {
            setMaintenanceDate(new Date().toISOString().split('T')[0]);
            setType('');
            setVendor('');
            setCost('');
            setDescription('');
        }
        setAttachedFile(null);
        setAttachmentMeta(null);
        setAutofillNotice(null);
        setError(null);
    }, [recordToEdit, isOpen]);

    const { user: authUser } = useSelector((state) => state.auth);
    const userId = authUser?.id;

    const maintenanceTypes = [
        { value: 'Repair', label: t('asset_details.maintenance_repair') },
        { value: 'Routine Service', label: t('asset_details.maintenance_routine_service') },
        { value: 'Hardware Upgrade', label: t('asset_details.maintenance_hardware_upgrade') },
        { value: 'Software Patch', label: t('asset_details.maintenance_software_patch') }
    ];

    const handleAutofill = (data, file) => {
        if (!file) {
            setAttachedFile(null);
            setAttachmentMeta(null);
            setAutofillNotice(null);
            return;
        }

        setAttachedFile(file);
        setAttachmentMeta(data);

        if (data.maintenance_date) setMaintenanceDate(data.maintenance_date);
        if (data.technician_name) setVendor(data.technician_name);
        if (typeof data.cost === 'number') setCost(String(data.cost));
        if (data.description) setDescription(data.description);

        // Pre-select document type to map to maintenance type if applicable
        if (data.document_type === 'Invoice') {
            setType('Repair');
        } else if (data.document_type === 'Warranty') {
            setType('Routine Service');
        }

        const extractedAssetId = data.asset_id?.trim();
        if (extractedAssetId && extractedAssetId !== assetTag) {
            setAutofillNotice(`AI extracted asset ID ${extractedAssetId}. Please verify it matches ${assetTag}.`);
        } else {
            setAutofillNotice('AI autofilled maintenance fields. Review the details below and log the maintenance.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!type || !maintenanceDate || !vendor || !cost || !description.trim()) {
            setError(t('asset_details.fill_required_fields'));
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            if (recordToEdit) {
                // Update existing record
                const { error: updateError } = await supabase
                    .from('maintenance')
                    .update({ 
                        maintenance_date: maintenanceDate, 
                        cost: parseFloat(cost), 
                        description, 
                        type, 
                        vendor,
                        title: `${type} - ${vendor}`,
                        performed_by: vendor
                    })
                    .eq('id', recordToEdit.id);

                if (updateError) throw updateError;

                await logActivity(
                    'maintenance_updated',
                    `Updated ${type} maintenance by ${vendor}. Cost: ${formatCurrency(cost)}`,
                    assetTag,
                    userId,
                    {
                        maintenance_id: recordToEdit.id,
                        type,
                        vendor,
                        cost: parseFloat(cost),
                        maintenance_date: maintenanceDate
                    }
                );

                onSuccess?.(`Maintenance record for ${assetTag} has been successfully updated.`);
            } else {
                // Insert new record and retrieve the database generated ID
                const { data: insertData, error: insertError } = await supabase
                    .from('maintenance')
                    .insert([{ 
                        asset_tag: assetTag, 
                        maintenance_date: maintenanceDate, 
                        cost: parseFloat(cost), 
                        description, 
                        type, 
                        vendor,
                        title: `${type} - ${vendor}`,
                        performed_by: vendor
                    }])
                    .select();

                if (insertError) throw insertError;

                const newMaintenanceId = insertData?.[0]?.id;

                // Handle file upload and attachment linking if a file was attached via AI
                if (attachedFile && newMaintenanceId) {
                    const uniqueFileName = `${Date.now()}_${attachedFile.name}`;
                    const filePath = `${assetTag}/${uniqueFileName}`;

                    // 1. Upload to Supabase Storage
                    const { error: uploadError } = await supabase.storage
                        .from('asset_attachments')
                        .upload(filePath, attachedFile);

                    if (uploadError) throw uploadError;

                    // 2. Insert into DB and link with maintenance_id
                    const { error: dbError } = await supabase
                        .from('asset_attachments')
                        .insert([
                            {
                                asset_tag: assetTag,
                                file_name: attachedFile.name,
                                file_path: filePath,
                                file_size: attachedFile.size,
                                file_type: attachedFile.type,
                                document_type: attachmentMeta?.document_type || 'Other',
                                description: description,
                                cost: cost ? parseFloat(cost) : 0,
                                date: maintenanceDate,
                                uploaded_by: userId,
                                reference_id: attachmentMeta?.reference_id || null,
                                maintenance_id: newMaintenanceId
                            },
                        ]);

                    if (dbError) throw dbError;
                }

                await logActivity(
                    'maintenance_logged',
                    `Logged ${type} maintenance by ${vendor}. Cost: ${formatCurrency(cost)}`,
                    assetTag,
                    userId,
                    {
                        type,
                        vendor,
                        cost: parseFloat(cost),
                        maintenance_date: maintenanceDate
                    }
                );

                onSuccess?.(`Maintenance record for ${assetTag} has been successfully logged.`);
            }
            
            // Reset form and close
            setMaintenanceDate(new Date().toISOString().split('T')[0]);
            setType('');
            setVendor('');
            setCost('');
            setDescription('');
            setAttachedFile(null);
            setAttachmentMeta(null);
            onClose();
        } catch (err) {
            console.error('Error logging maintenance:', err);
            setError(err.message || t('asset_details.log_maintenance_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full relative z-10 overflow-hidden max-h-[90vh] flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-border bg-primary/5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 text-primary">
                                    <div className="p-2 bg-primary/10 rounded-lg">
                                        <Icon name="Wrench" size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-foreground">
                                            {recordToEdit ? 'Edit Maintenance Record' : t('asset_details.log_maintenance')}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">{t('asset_details.log_maintenance_subtitle', { tag: assetTag })}</p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    iconName="X"
                                    onClick={onClose}
                                />
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
                            {!recordToEdit && <AIAttachmentUploader assetTag={assetTag} onSuccess={onSuccess} onAutofill={handleAutofill} />}

                            {autofillNotice && (
                                <div className="p-3 bg-warning/10 border border-warning/20 rounded-md flex items-center gap-2 text-warning text-sm">
                                    <Icon name="BadgeAlert" size={16} />
                                    {autofillNotice}
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <Input
                                    label={t('asset_details.maintenance_date')}
                                    type="date"
                                    value={maintenanceDate}
                                    onChange={(e) => setMaintenanceDate(e.target.value)}
                                    required
                                />
                                <Select
                                    label={t('asset_details.maintenance_type')}
                                    options={maintenanceTypes}
                                    value={type}
                                    onChange={setType}
                                    placeholder={t('asset_details.select_type')}
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <Input
                                    label={t('asset_details.vendor_technician')}
                                    placeholder={t('asset_details.who_did_the_work')}
                                    value={vendor}
                                    onChange={(e) => setVendor(e.target.value)}
                                    required
                                />
                                <Input
                                    label={t('labels.purchase_price')}
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={cost}
                                    onChange={(e) => setCost(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">
                                    {t('asset_details.description_of_work')} <span className="text-error">*</span>
                                </label>
                                <textarea
                                    className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
                                    placeholder={t('asset_details.describe_work')}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    required
                                />
                            </div>

                            {error && (
                                <div className="p-3 bg-error/10 border border-error/20 rounded-md flex items-center gap-2 text-error text-sm">
                                    <Icon name="AlertCircle" size={16} />
                                    {error}
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t border-border">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    iconName="Save"
                                    loading={isSubmitting}
                                >
                                    {recordToEdit ? 'Save Changes' : t('asset_details.log_maintenance')}
                                </Button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AddMaintenanceModal;
